// Тексты статей документации. Названия кнопок и разделов взяты из интерфейсов
// приложений: правите надпись в приложении — правьте и здесь.

import { ANDROID_MIN, IOS_MIN, WIN_MIN } from '@/data/downloads';
import { CONTACTS, PARTNER_PRICE_RUB, PARTNER_TRIAL_DAYS, PRICE_FROM_RUB, TRIAL_DAYS } from '@/seo/site';
import { formatRub, pluralRu } from '@/utils/format';
import type { DocBlock, DocContent } from './types';

const p = (text: string): DocBlock => ({ kind: 'p', text });
const h3 = (text: string): DocBlock => ({ kind: 'h3', text });
const steps = (...items: string[]): DocBlock => ({ kind: 'steps', items });
const list = (...items: string[]): DocBlock => ({ kind: 'list', items });
const note = (text: string): DocBlock => ({ kind: 'note', tone: 'info', text });
const warn = (text: string): DocBlock => ({ kind: 'note', tone: 'warning', text });
const table = (head: string[], ...rows: string[][]): DocBlock => ({ kind: 'table', head, rows });

const PRICE = formatRub(PRICE_FROM_RUB);
const PARTNER_PRICE = formatRub(PARTNER_PRICE_RUB);
const TRIAL = `${TRIAL_DAYS} ${pluralRu(TRIAL_DAYS, 'день', 'дня', 'дней')}`;
const PARTNER_TRIAL = `${PARTNER_TRIAL_DAYS} ${pluralRu(PARTNER_TRIAL_DAYS, 'день', 'дня', 'дней')}`;
const EMAIL = `[${CONTACTS.supportEmail}](mailto:${CONTACTS.supportEmail})`;

export const DOC_CONTENT: Record<string, DocContent> = {
  start: {
    lead: 'Запустить заведение в Resstro можно самостоятельно, без выезда инженера. Понадобится браузер для админ-панели и устройство для кассы: iPad, Android-планшет или терминал либо компьютер с Windows.',
    facts: [
      { label: 'Пробный период', value: `${TRIAL}, без карты` },
      { label: 'Админ-панель', value: '[admin.resstro.ru](https://admin.resstro.ru)' },
      { label: 'Касса', value: '[Скачать приложение](/downloads)' },
    ],
    sections: [
      {
        id: 'signup',
        title: 'Регистрация',
        blocks: [
          steps(
            'Откройте [страницу регистрации](/signup) и укажите название заведения, город, адрес, email и телефон. Нажмите **Зарегистрироваться**.',
            'Введите код из письма. Откроется личный кабинет на resstro.ru.',
            'Из кабинета перейдите в админ-панель: вход произойдёт без повторного ввода кода.',
          ),
          note(`Первые ${TRIAL} доступны все возможности. Карта при регистрации не нужна, автосписание не включается.`),
        ],
      },
      {
        id: 'venue',
        title: 'Профиль заведения',
        blocks: [
          p('На странице **Главная** админ-панели есть чек-лист запуска: заполнить профиль, добавить категории и блюда, подключить кассу.'),
          steps(
            'Откройте **Настройки → Заведение** и проверьте название, адрес, телефон и часовой пояс.',
            'Если точек несколько, добавьте остальные: **Настройки → Новое заведение**.',
          ),
        ],
      },
      {
        id: 'menu',
        title: 'Меню',
        blocks: [
          steps(
            'Создайте категории: **Меню → Категории → Категория**.',
            'Добавьте блюда: **Меню → Блюда → Блюдо**. Укажите название, цену, категорию и, если есть, фото.',
            'Чтобы кухня и бар получали только свои позиции, заведите разделы в **Меню → Разделы** и выберите раздел в карточке блюда.',
          ),
          note('Меню уже есть в iiko, r_keeper, Poster или Restik? Загрузите его файлом через **Меню → Импорт**. Бумажное меню можно сфотографировать: **Меню → Сканер**. Подробнее в статье [Админ-панель](/docs/admin#menu).'),
        ],
      },
      {
        id: 'cashier',
        title: 'Касса',
        blocks: [
          steps(
            'Установите приложение «Resstro Касса» со страницы [Загрузки](/downloads): для iPad, Android или Windows.',
            'В админ-панели откройте **Устройства → Кассы → Подключить кассу**. Появится 6-значный код.',
            'Введите код на кассе. Касса покажет «Привязано к» и название заведения.',
          ),
          warn('Код действует 5 минут и срабатывает один раз. Не успели — сгенерируйте новый.'),
        ],
      },
      {
        id: 'first-order',
        title: 'Первый заказ',
        blocks: [
          steps(
            'На кассе откройте вкладку **Смена**, укажите имя кассира и наличные в кассе, нажмите **Открыть смену**.',
            'Во вкладке **Зал** добавьте столы. Их же увидят официанты в своём приложении.',
            'Откройте стол, выберите блюда и нажмите **Добавить к столу**: на принтеры кухни и бара уйдут кухонные листы.',
            'Нажмите **Пробить**, выберите способ оплаты и подтвердите. Заказ и оплата сразу появятся в админ-панели, в разделе **Текущая касса**.',
          ),
        ],
      },
      {
        id: 'next',
        title: 'Что дальше',
        blocks: [
          list(
            'Подключить принтеры чеков и кухни: [Оборудование](/docs/hardware#printers).',
            'Принимать заказы и оплату с телефона гостя: [Электронное меню](/docs/qr-menu).',
            'Добавить сотрудников и настроить права: [Сотрудники и роли](/docs/admin#users).',
            'Подключить официантов: [Приложение официанта](/docs/waiter).',
            'Оплатить подписку после пробного периода: [Подписка и оплата](/docs/billing).',
          ),
        ],
      },
    ],
  },

  billing: {
    lead: `Resstro работает по подписке: ${PRICE} в месяц за заведение, без процента с оборота. Оплачивает и меняет подписку только владелец аккаунта.`,
    facts: [
      { label: 'Тариф', value: `${PRICE} в месяц за заведение` },
      { label: 'Пробный период', value: TRIAL },
      { label: 'Где оплатить', value: '**Подписка и оплата**' },
    ],
    sections: [
      {
        id: 'trial',
        title: 'Пробный период',
        blocks: [
          p(`После регистрации ${TRIAL} доступны все возможности. Карта не нужна, автосписание не включается: решение об оплате вы принимаете после теста.`),
        ],
      },
      {
        id: 'plan',
        title: 'Что входит в тариф',
        blocks: [
          list(
            `Одно заведение с одной кассой — ${PRICE} в месяц.`,
            `Каждое следующее заведение — ещё ${PRICE} в месяц. Все точки в одном кабинете.`,
            'Оплатить можно на месяц или сразу на год: годовая подписка выходит дешевле.',
            'Лимиты по заведениям, кассам и сотрудникам видны на странице **Подписка и оплата**.',
          ),
          p('Отдельно подключаются модули, например «Официанты» для приложения официанта. Актуальные цены — в разделе [Цена](/#pricing) на главной.'),
        ],
      },
      {
        id: 'pay',
        title: 'Оплатить или продлить',
        blocks: [
          steps(
            'В админ-панели откройте **Подписка и оплата**. Раздел виден только владельцу.',
            'Нажмите **Оплатить подписку** или **Продлить**, если подписка ещё действует.',
            'Оплатите на странице ЮKassa. Статус обновится, а платёж появится в разделе **История платежей**.',
          ),
        ],
      },
      {
        id: 'change',
        title: 'Сменить период или отключить автопродление',
        blocks: [
          steps(
            'Откройте **Подписка и оплата → Сменить тариф**.',
            'Выберите период, месяц или год, и нажмите **Перейти** или **Продлить**.',
          ),
          p('Чтобы оплата не списывалась автоматически, нажмите **Отменить авто-продление**.'),
        ],
      },
      {
        id: 'statuses',
        title: 'Статусы подписки',
        blocks: [
          table(
            ['Статус', 'Что это значит'],
            ['Пробный период', `Первые ${TRIAL} после регистрации, доступно всё`],
            ['Активна', 'Подписка оплачена, всё работает'],
            ['Ожидает оплаты', 'Оплаченный период закончился, подписку нужно продлить'],
            ['Отменена', 'Подписка отменена'],
          ),
        ],
      },
      {
        id: 'unpaid',
        title: 'Если не оплатить вовремя',
        blocks: [
          p('После окончания оплаченного периода есть три дня льготного периода с уведомлениями. Затем админ-панель переходит в режим просмотра, а касса и электронное меню временно блокируются.'),
          note('Данные хранятся 90 дней. Как только вы оплатите подписку, работа продолжится с того же места.'),
        ],
      },
      {
        id: 'documents',
        title: 'Документы и возврат',
        blocks: [
          p(`Счёт, акт и договор оферты выдаём по запросу на ${EMAIL}. Условия возврата описаны в документе [Возврат средств](/legal/return), условия сервиса — в [публичной оферте](/legal/offer).`),
        ],
      },
      {
        id: 'partners',
        title: 'Поставщики и оптовики',
        blocks: [
          p(`Кабинеты [поставщика](/docs/supplier) и [оптовика](/docs/wholesaler) оплачиваются отдельно от тарифа заведения: ${PARTNER_TRIAL} бесплатно, затем ${PARTNER_PRICE} в месяц.`),
        ],
      },
    ],
  },

  admin: {
    lead: 'Админ-панель — рабочее место владельца и администратора: здесь заводят меню, сотрудников и кассы, следят за сменой и смотрят отчёты. Открывается в браузере на компьютере, планшете или телефоне.',
    facts: [
      { label: 'Адрес', value: '[admin.resstro.ru](https://admin.resstro.ru)' },
      { label: 'Кто входит', value: 'Владелец и администратор' },
      { label: 'Вход', value: 'Код из письма' },
    ],
    sections: [
      {
        id: 'login',
        title: 'Вход',
        blocks: [
          steps(
            'Откройте [admin.resstro.ru](https://admin.resstro.ru), введите email и нажмите **Получить код**.',
            'Введите код из письма и нажмите **Войти**. Письмо не пришло — запросите код повторно.',
          ),
          p('Аккаунта ещё нет — [зарегистрируйтесь на resstro.ru](/signup). Кассиры и официанты в админ-панель не входят: у них свои приложения.'),
          h3('Несколько заведений'),
          p('Новая точка добавляется в **Настройки → Новое заведение**: укажите название и нажмите **Создать**. Переключаться между заведениями можно селектором **Заведение** в боковом меню.'),
        ],
      },
      {
        id: 'sections',
        title: 'Разделы',
        blocks: [
          table(
            ['Раздел', 'Для чего'],
            ['Главная', 'Чек-лист запуска и показатели дня: заказы, выручка, средний чек, топ-5 блюд'],
            ['Текущая касса', 'Открытая смена в реальном времени: выручка, активные заказы, оплаты, расходы'],
            ['Пользователи', 'Сотрудники и права ролей'],
            ['Меню', 'Категории, разделы, блюда, модификаторы, сканер и импорт'],
            ['Заказы', 'Все заказы с фильтрами, отмена заказа и смена способа оплаты'],
            ['Аналитика', 'Выручка, средний чек и популярные позиции за период'],
            ['Расходы', 'Расходы заведения и статьи расходов'],
            ['Смены', 'История смен и подробный отчёт по каждой'],
            ['Отчёты', 'Отчёт по продажам за период с выгрузкой в CSV и PDF'],
            ['Склад', 'Позиции, поставки, тех-карты, заготовки, инвентаризация'],
            ['Устройства', 'Подключение касс и телефонов официантов'],
            ['Подписка и оплата', 'Тариф, продление и история платежей, только для владельца'],
            ['Приложение', 'Карточка заведения в гостевом приложении Resstro People'],
            ['Настройки', 'Заведение, меню и бренд, оплаты, касса'],
          ),
        ],
      },
      {
        id: 'menu',
        title: 'Меню',
        blocks: [
          h3('Категории'),
          p('**Меню → Категории → Категория**: название и порядок. Переключатели **Активна** и **Активна в электронном меню** управляют тем, где видна категория. Для акции задайте **Скидка на всю категорию, %**.'),
          h3('Разделы'),
          p('Раздел определяет, на какой принтер уйдёт блюдо: кухня, бар, кальянная. Разделы создаются в **Меню → Разделы → Раздел**, а принтер получает свои разделы в настройках кассы.'),
          h3('Блюда'),
          p('**Меню → Блюда → Блюдо**: название, описание, цена, закупочная цена, раздел, категория, до 10 фото и модификаторы. Закончилось блюдо — выключите **Доступно для заказа**, а когда оно появится снова, включите.'),
          p('Блюдо, которое уже продавалось, при удалении уходит в архив: история продаж и отчёты не пострадают.'),
          h3('Модификаторы'),
          p('Добавки и соусы заводятся в **Меню → Модификаторы → Модификатор**: название и цена. К блюду модификаторы привязываются в его карточке.'),
          h3('Сканер и импорт'),
          list(
            '**Меню → Сканер**: нажмите **Сфотографировать меню** или **Загрузить файл**, проверьте распознанные позиции и цены и нажмите **Импортировать**. Размеры, например 0,3 и 0,5 л, станут отдельными блюдами.',
            '**Меню → Импорт**: файл CSV или XLSX до 15 МБ из iiko, r_keeper, Restik, Poster или обычной таблицы. Сопоставьте колонки, нажмите **Проверить без записи**, затем **Импортировать**. Так же переносятся тех-карты, склад и столы.',
          ),
        ],
      },
      {
        id: 'users',
        title: 'Сотрудники и роли',
        blocks: [
          table(
            ['Роль', 'Где работает', 'Как входит'],
            ['Владелец', 'Админ-панель, Resstro Admin', 'Код из письма'],
            ['Администратор', 'Админ-панель, Resstro Admin', 'Код из письма'],
            ['Кассир', 'Resstro Касса', 'Касса подключена к заведению кодом, кассир указывает имя при открытии смены'],
            ['Официант', 'Resstro Официант', 'Код из **Устройства → Официанты**'],
          ),
          h3('Добавить сотрудника'),
          steps(
            'Откройте **Пользователи → Добавить**.',
            'Заполните имя, email, телефон, роль и заведение, задайте пароль. Кассиру можно ограничить **Максимальная скидка (%)**.',
            'Нажмите **Создать**.',
          ),
          p('Права каждой роли настраиваются на вкладке **Права ролей**: что видят и что могут делать администратор, кассир и официант. Менять права может только владелец.'),
          p('Сотрудник уволился — нажмите **Заблокировать** в его карточке. Заблокированный официант сразу теряет доступ со своего телефона.'),
        ],
      },
      {
        id: 'devices',
        title: 'Устройства',
        blocks: [
          h3('Касса'),
          steps(
            'Откройте **Устройства → Кассы → Подключить кассу**.',
            'Введите 6-значный код на экране кассы или скопируйте его кнопкой **Скопировать код** и отправьте кассиру.',
          ),
          warn('Код действует 5 минут и срабатывает один раз. Если он истёк, нажмите **Сгенерировать новый**.'),
          h3('Телефон официанта'),
          p('Официанты подключаются в **Устройства → Официанты → Подключить официанта**. Подробно — в статье [Официант](/docs/waiter#connect).'),
          h3('Отозвать доступ'),
          p('Потеряли планшет или сотрудник ушёл: нажмите кнопку блокировки у устройства, выберите **Отозвать доступ** для кассы или **Отключить телефон** для официанта, укажите причину и подтвердите. Устройство потеряет доступ, даже если сейчас оно без связи: сессия закроется при следующем выходе в сеть.'),
        ],
      },
      {
        id: 'shifts',
        title: 'Смены и отчёты',
        blocks: [
          p('Смену открывает и закрывает кассир на кассе. Открытая смена видна в разделе **Текущая касса** и обновляется каждые несколько секунд, закрытые собраны в разделе **Смены**.'),
          h3('Отчёт по смене'),
          steps(
            'Откройте **Смены** и нажмите на значок просмотра у нужной смены.',
            'В отчёте — выручка, касса наличных (остаток на открытие, приход, возвраты, закуп, расходы, сколько должно быть и сколько оказалось), выплаты, снятые позиции, оплаты по способам и почасовая активность.',
            'Нажмите **Скачать PDF**, **Печать** или **Email**.',
          ),
          p('Забыли провести расход в смене — добавьте его кнопкой **Внести забытый расход**.'),
          h3('Отчёты и аналитика'),
          p('В разделе **Отчёты** выберите период: **Сегодня**, **7 дней**, **30 дней**, **Этот месяц** или свои даты. Нажмите **Обновить**, выгрузка — в **CSV** и **PDF**. Раздел **Аналитика** показывает графики выручки, среднего чека и популярных позиций.'),
        ],
      },
      {
        id: 'warehouse',
        title: 'Склад и тех-карты',
        blocks: [
          list(
            '**Склад → Позиции → Позиция**: название, единица учёта, минимальный остаток и цена.',
            'Приход, расход и списание вручную: кнопка **Приход/Расход**, тип движения и **Провести**. Причины списания: порча, истёк срок, бой или брак, питание персонала, проработка.',
            '**Склад → Поставки → Новая поставка**: позиции и оплата — без оплаты, наличными из кассы или внешней оплатой, затем **Провести поставку**. Наличные из кассы можно взять только при открытой смене.',
            '**Склад → Тех-карты**: состав блюда, себестоимость и фудкост. По тех-картам склад списывается автоматически при продаже.',
            '**Склад → Заготовки → Новая заготовка**: полуфабрикаты со своим составом.',
            '**Склад → Инвентаризация → Новая инвентаризация**: внесите фактические остатки, нажмите **Создать черновик**, затем **Провести**.',
          ),
        ],
      },
      {
        id: 'settings',
        title: 'Настройки',
        blocks: [
          table(
            ['Вкладка', 'Что настраивается'],
            ['Заведение', 'Название, адрес, телефон, валюта и часовой пояс'],
            ['Меню и бренд', 'Цвета, шрифт и иконки электронного меню, свой домен меню'],
            ['Оплаты', 'Способы оплаты на кассе, QR-код для перевода, онлайн-оплата из меню через ЮKassa'],
            ['Касса', 'Лимит скидки кассира, банковский терминал, менюборд для телевизора, магазин «Закуп»'],
          ),
          p('Подробнее: [онлайн-оплата](/docs/qr-menu#payments), [банковский терминал](/docs/hardware#terminal), [меню на телевизоре](/docs/hardware#tv).'),
        ],
      },
      {
        id: 'app-card',
        title: 'Карточка в Resstro People',
        blocks: [
          p('Раздел **Приложение** отвечает за то, как заведение выглядит в гостевом приложении [Resstro People](/docs/people): обложки, кухни и другие данные карточки.'),
        ],
      },
    ],
  },

  'admin-app': {
    lead: 'Resstro Admin — приложение для iPhone и iPad с теми же разделами, что и веб-админка. Удобно, когда нужно проверить выручку, остатки или поправить меню не за компьютером.',
    facts: [
      { label: 'Устройства', value: 'iPhone и iPad' },
      { label: 'Кто входит', value: 'Владелец и администратор' },
      { label: 'Вход', value: 'Код из письма' },
    ],
    sections: [
      {
        id: 'login',
        title: 'Установка и вход',
        blocks: [
          steps(
            'Установите Resstro Admin из App Store.',
            'Войдите по коду из письма, с тем же email, что и в веб-админке.',
          ),
          p('С iPhone или iPad приложение можно открыть и из личного кабинета на resstro.ru: вход произойдёт без ввода кода.'),
        ],
      },
      {
        id: 'navigation',
        title: 'Навигация',
        blocks: [
          p('На iPhone внизу экрана вкладки **Главная**, **Заказы**, **Касса**, **Меню** и **Ещё**. В **Ещё** собраны остальные разделы по группам: работа, деньги, каталог, аналитика, управление. На iPad разделы открываются в боковом меню, как в браузере.'),
          p('Если заведений несколько, переключайтесь между ними так же, как в веб-админке.'),
        ],
      },
      {
        id: 'features',
        title: 'Что можно делать',
        blocks: [
          list(
            'Смотреть открытую смену в реальном времени: выручку, заказы и оплаты.',
            'Работать с заказами, расходами, сменами и отчётами.',
            'Править меню: блюда, до 10 фото, сканер меню камерой телефона и импорт из iiko, r_keeper, Restik и Poster.',
            'Вести склад и тех-карты.',
            'Подключать кассы и телефоны официантов, настраивать терминал и менюборд.',
            'Оплачивать подписку — это доступно владельцу.',
          ),
        ],
      },
      {
        id: 'notifications',
        title: 'Уведомления',
        blocks: [
          p('Приложение присылает уведомления о заказах, сменах и заканчивающихся остатках на складе. Когда подписка подходит к концу, вверху появляется напоминание с кнопкой **Продлить**.'),
        ],
      },
    ],
  },

  cashier: {
    lead: '«Resstro Касса» — рабочее место кассира. Приложение работает на iPad и iPhone, Android-планшетах и терминалах и на компьютерах с Windows, а все заказы и оплаты сразу видны в админ-панели.',
    facts: [
      { label: 'Платформы', value: `${IOS_MIN}, ${ANDROID_MIN}, ${WIN_MIN}` },
      { label: 'Скачать', value: '[Загрузки](/downloads)' },
      { label: 'Вкладки', value: 'Зал, Заказы, Смена, Закуп, Настройки' },
    ],
    sections: [
      {
        id: 'pairing',
        title: 'Подключение к заведению',
        blocks: [
          steps(
            'Установите приложение со страницы [Загрузки](/downloads).',
            'В админ-панели откройте **Устройства → Кассы → Подключить кассу**. Появится 6-значный код.',
            'Введите код на экране кассы. После шестой цифры он отправится сам, а касса покажет «Привязано к» и название заведения.',
          ),
          note('Отвязать кассу: на iPad — **Настройки → Выйти**, на Android — **Выйти из устройства**, на Windows — **Отвязать кассу**. Чтобы подключить её снова, понадобится новый код.'),
        ],
      },
      {
        id: 'shift',
        title: 'Смена',
        blocks: [
          p('Пока смена не открыта, зал закрыт. Имя кассира попадает в чек и в отчёт по смене.'),
          h3('Открыть смену'),
          steps(
            'Откройте вкладку **Смена**.',
            'Укажите, кто на смене, сколько наличных в кассе и, если нужно, комментарий.',
            'Нажмите **Открыть смену**.',
          ),
          h3('Закрыть смену'),
          steps(
            'Проверьте **Итоги**: выручку по способам оплаты и число заказов.',
            'В блоке **Пересчёт кассы** введите, сколько денег фактически в ящике. Касса сравнит сумму с ожидаемой и покажет, сходится ли касса, есть ли излишек или недостача.',
            'Нажмите **Закрыть смену** и подтвердите.',
          ),
          warn('Перед закрытием убедитесь, что все чеки пробиты. Подробный отчёт по закрытой смене смотрите в админ-панели: **Смены → Отчёт по смене**.'),
        ],
      },
      {
        id: 'hall',
        title: 'Зал и вынос',
        blocks: [
          p('Вверху переключатель **Зал** и **Вынос**. Стол показывает статус: свободен, занят или оплачен. Заказ, который гость уже оплатил в приложении, отмечен значком телефона.'),
          list(
            'На iPad и Windows столы расставляются перетаскиванием. Долгое нажатие на стол открывает действия **Открыть стол**, **Пробить заказ** и **Удалить столик**.',
            'На Android столы идут сеткой по зонам, новый стол добавляется кнопкой **Добавить стол**.',
          ),
        ],
      },
      {
        id: 'order',
        title: 'Заказ',
        blocks: [
          steps(
            'Откройте стол. Блюда выбираются по категориям или через поиск.',
            'Если у блюда есть добавки, отметьте нужные и нажмите **Добавить в заказ**.',
            'Чтобы передать пожелание, нажмите **Добавить комментарий**, например «без лука».',
            'Нажмите **Добавить к столу**: заказ сохранится, а на принтеры кухни и бара уйдут кухонные листы.',
          ),
          list(
            '**Пречек** печатает предварительный счёт для гостя.',
            '**Перенести на другой стол** пересаживает гостей вместе с заказом.',
            '**Отменить заказ** удаляет все позиции, а на кухне печатается лист «ОТМЕНА ЗАКАЗА».',
          ),
          note('Отдельной кнопки «на кухню» нет: кухонный лист печатается при **Добавить к столу**, если у принтера выбрана печать при добавлении. Настройка — в статье [Оборудование](/docs/hardware#printers).'),
          h3('Вынос'),
          p('Для заказа навынос укажите телефон клиента и адрес доставки: кнопка **Адрес и телефон**.'),
        ],
      },
      {
        id: 'payment',
        title: 'Оплата',
        blocks: [
          steps(
            'В заказе нажмите **Пробить**.',
            'Выберите способ оплаты. Если кассиру разрешены скидки, выберите размер скидки.',
            'Нажмите **Пробить** с суммой заказа. Чек уйдёт на принтер.',
          ),
          table(
            ['Способ', 'Что делает кассир'],
            ['Наличные', 'Вводит, сколько получено, касса считает сдачу'],
            ['Карта', 'Сумма уходит на подключённый терминал, или кассир проводит карту на терминале сам'],
            ['Перевод', 'Дожидается зачисления и пробивает чек'],
            ['QR-код', 'Показывает гостю QR для оплаты в приложении банка. Картинку QR загружают в админ-панели'],
            ['Смешанная', 'Делит сумму между наличными, картой, переводом и QR-кодом, части в сумме равны итогу'],
          ),
          p('Ошиблись со способом оплаты — администратор исправит его в админ-панели: раздел **Заказы**, карточка заказа, **Изменить способ оплаты**. Онлайн-оплату изменить нельзя.'),
        ],
      },
      {
        id: 'guest-orders',
        title: 'Заказы гостей',
        blocks: [
          p('Заказы из электронного меню и приложения Resstro People появляются на кассе со звуковым сигналом и счётчиком на вкладке зоны. Сигнал включается переключателем **Звук на новые заказы** в настройках кассы, а кнопка **Проверить звук** проигрывает его.'),
        ],
      },
      {
        id: 'supplies',
        title: 'Закуп и списание',
        blocks: [
          p('Вкладка **Закуп** нужна для поставок и списаний прямо с кассы.'),
          list(
            '**Новая поставка**: поставщик, позиции и оплата наличными или переводом из кассы. Поставка попадает на склад.',
            '**Списать**: причина, позиции и комментарий. Без открытой смены списание не пройдёт.',
            '**Магазин** — каталог поставщиков Resstro: корзина, заказ и приёмка товара. Магазин включён по умолчанию, отключается в админ-панели: **Настройки → Касса**.',
          ),
        ],
      },
      {
        id: 'offline',
        title: 'Без интернета',
        blocks: [
          p('Если пропала связь, вверху появится «Нет связи · работаем локально». Смены, заказы и оплаты сохраняются на устройстве, чеки и кухонные листы печатаются сразу, а очередь уйдёт на сервер, когда сеть вернётся.'),
          list(
            'Сколько операций ждёт отправки, видно в плашке над залом и в **Настройки → Синхронизация**.',
            '**Синхронизировать сейчас** отправляет очередь вручную.',
            'Магазин «Закуп» без интернета не работает.',
          ),
          warn('Не нажимайте **Очистить очередь**, пока операции не отправлены: неотправленные заказы и оплаты пропадут.'),
        ],
      },
      {
        id: 'platforms',
        title: 'Отличия платформ',
        blocks: [
          table(
            ['Возможность', 'iPad и iPhone', 'Android', 'Windows'],
            ['Столы', 'Перетаскиванием', 'Сеткой', 'Перетаскиванием'],
            ['Принтеры', 'Wi-Fi, LAN, Bluetooth LE', 'Сеть, Bluetooth', 'Wi-Fi, LAN, USB, COM'],
            ['Выход', '**Выйти**', '**Выйти из устройства**', '**Отвязать кассу**'],
            ['Только здесь', '—', '—', 'Полноэкранный режим, автозапуск, автообновление'],
          ),
        ],
      },
    ],
  },

  waiter: {
    lead: '«Resstro Официант» — приложение для телефона официанта. Официант выбирает стол, добавляет блюда с комментарием для кухни и отправляет заказ на кассу, а касса печатает кухонные листы и принимает оплату.',
    facts: [
      { label: 'Телефон', value: 'Android, установка APK' },
      { label: 'Скачать', value: '[Загрузки](/downloads)' },
      { label: 'Модуль', value: '«Официанты», подключается отдельно' },
    ],
    sections: [
      {
        id: 'module',
        title: 'Подключение модуля',
        blocks: [
          p('Приложение работает с модулем «Официанты». Владелец подключает его в админ-панели: **Устройства → Официанты → Подключить официантов**. Модуль оплачивается до конца текущего периода подписки.'),
        ],
      },
      {
        id: 'install',
        title: 'Установка',
        blocks: [
          steps(
            'Скачайте APK на телефон официанта со страницы [Загрузки](/downloads).',
            'Откройте файл. Android спросит разрешение на установку из этого источника — разрешите.',
            'Подтвердите установку и откройте приложение.',
          ),
        ],
      },
      {
        id: 'connect',
        title: 'Подключение телефона',
        blocks: [
          steps(
            'Администратор открывает **Устройства → Официанты → Подключить официанта**, выбирает официанта из списка или вписывает имя нового и нажимает **Получить код**.',
            'Официант вводит 6-значный код в поле «Код подключения» и нажимает **Подключить**.',
          ),
          note('Код действует 5 минут. Если кода нет, можно войти по почте: **Войти по почте**, затем **Получить код** и код из письма.'),
        ],
      },
      {
        id: 'tables',
        title: 'Столы',
        blocks: [
          p('На экране **Столы** есть фильтр **Все** и **Мои** и переключатель **Зал** и **Навынос**. У стола видно, свободен ли он, чей это заказ, оплачен ли он и сколько позиций ещё не отправлено.'),
          p('Столы заводит кассир в приложении кассы. Список обновляется сам, а чтобы обновить его сразу, потяните вниз.'),
        ],
      },
      {
        id: 'order',
        title: 'Заказ',
        blocks: [
          steps(
            'Откройте стол и добавьте блюда из меню. У блюда можно выбрать **Добавки** и написать **Комментарий для кухни**.',
            'Нажмите **Отправить на кассу**. Приложение покажет «Заказ №… открыт и отправлен на кассу».',
            'Касса печатает кухонные листы, а заказ появляется в зале у кассира.',
          ),
          p('Блок **На кассе** показывает уже отправленные позиции, ниже — черновик, который ещё не отправлен.'),
        ],
      },
      {
        id: 'actions',
        title: 'Оплата и другие действия',
        blocks: [
          p('Долгое нажатие на стол или кнопка «⋯» открывает действия, доступные по правам роли: **Оплатить**, **Сменить официанта**, **Поменять стол**, **Комментарий**, **Распечатать чек**, **Удалить**.'),
          list(
            'Оплата: **Оплатить**, способ оплаты, **Принять оплату**. Платёж попадёт в смену кассы с отметкой, что его принял официант, а наличные остаются у официанта.',
            'Для оплаты по QR приложение покажет гостю QR-код.',
            '**Распечатать чек** отправляет чек на принтер кассы.',
          ),
        ],
      },
      {
        id: 'limits',
        title: 'Ограничения',
        blocks: [
          list(
            'Отправить заказ можно только при открытой смене на кассе. Иначе приложение покажет «Смена на кассе закрыта — заказы отправить нельзя».',
            'После оплаты стола новые позиции можно добавить, когда кассир закроет стол. Снять позиции из оплаченного заказа может только касса.',
            'Без связи с сервером видны последние загруженные столы, но отправить заказ нельзя.',
          ),
        ],
      },
    ],
  },

  hardware: {
    lead: 'Касса работает и без принтера: заказы и оплаты не теряются. Для кухонных листов и чеков понадобится ESC/POS-принтер, для оплаты картой — банковский терминал.',
    facts: [
      { label: 'Принтеры', value: 'ESC/POS, лента 58 или 80 мм' },
      { label: 'Печать на кассе', value: '**Настройки → Печать**' },
      { label: 'Терминалы', value: 'Сбер, Inpas, Arcus, СБП QR' },
    ],
    sections: [
      {
        id: 'printers',
        title: 'Принтеры',
        blocks: [
          p('Подходят ESC/POS-принтеры чеков, например Xprinter, Epson TM, Star и Bixolon. Способ подключения зависит от кассы.'),
          table(
            ['Касса', 'Подключение'],
            ['iPad и iPhone', 'Wi-Fi или LAN, Bluetooth LE. Обычный Bluetooth на iPad не поддерживается'],
            ['Android', 'Сеть и Bluetooth, в том числе обычный'],
            ['Windows', 'Wi-Fi или LAN, USB, COM'],
          ),
          h3('Добавить принтер'),
          steps(
            'На кассе откройте **Настройки → Печать → Принтеры**.',
            'Запустите поиск: на iPad — **Поиск принтера**, на Android — **Подключить принтер**, на Windows — **Найти принтер** или **Добавить по IP-адресу**.',
            'Касса найдёт сетевые принтеры по портам 9100, 9101 и 631. Если принтер не нашёлся, просканируйте диапазон IP-адресов.',
            'Выберите принтер и нажмите **Тест**.',
          ),
          h3('Что и когда печатать'),
          list(
            '**Когда печатать**: при добавлении в заказ — для кухонных листов, при оплате — для чеков.',
            '**Кухонный лист**: крупный шрифт и без цен.',
            '**Разделы меню**: какие разделы печатает этот принтер. Так кухня и бар получают только свои блюда.',
            '**Ручная печать чека**: чек печатается только по кнопке **Пречек**.',
            '**Лента**: 58 или 80 мм.',
            '**Кодовая страница**: PC866 или Windows-1251.',
          ),
          note('Вместо русских букв печатаются непонятные символы — смените кодовую страницу и напечатайте тест. Для принтеров без кириллицы есть растровая печать.'),
        ],
      },
      {
        id: 'terminal',
        title: 'Банковский терминал',
        blocks: [
          p('Режим работы с терминалом общий для всех касс заведения и задаётся в админ-панели: **Настройки → Касса**, блок «Банковский терминал». Касса подхватит настройки при следующем входе.'),
          table(
            ['Режим', 'Как работает'],
            ['Без эквайринга', 'Кнопки «Карта» на кассе нет: только наличные, перевод и QR'],
            ['Вручную', 'Кассир проводит оплату на терминале сам, касса отмечает «Карта»'],
            ['Сбер UPOS', 'Касса отправляет сумму на терминал Сбера. Терминал должен быть в той же сети, что и касса'],
            ['Inpas', 'Через сервис Inpas Dual Connector на кассе: терминалы Т-Банка, Альфа-Банка, Райффайзенбанка, ВТБ, ПСБ'],
            ['Arcus 2', 'Газпромбанк, Уралсиб, Открытие. На кассе нужна библиотека Arcus'],
            ['СБП QR', 'Оплата по QR-коду СБП без терминала'],
          ),
          p('Для Сбера и Inpas укажите адрес терминала или сервиса, порт, ID терминала (TID) и таймаут. Если у терминала нет своего принтера, включите **Печатать слип**: касса напечатает банковский слип вместе с чеком.'),
          p('При оплате картой касса ждёт ответа терминала. Если ответ не пришёл вовремя, касса спросит, прошла ли оплата, и зафиксирует результат без двойного списания.'),
          note('Передача суммы на терминал проверена на кассах для iPad и Windows с терминалами Сбера и на Windows с Inpas. Если сомневаетесь, подойдёт ли ваш терминал, напишите в поддержку.'),
          h3('QR-код для перевода'),
          p('Чтобы касса показывала гостю QR-код вашего банка, включите способ оплаты «QR-код» и загрузите картинку в **Настройки → Оплаты**, блок «QR-код для перевода».'),
        ],
      },
      {
        id: 'guest-screen',
        title: 'Экран гостя',
        blocks: [
          p('Второй экран показывает гостю состав заказа, скидку и итог, при оплате — способ и QR-код, а после оплаты — «Спасибо». Режим выбирается на кассе: **Настройки → Экран гостя**.'),
          list(
            '**Внешний дисплей** — монитор по HDMI или AirPlay, подключается автоматически.',
            '**Транслировать на второй планшет** — второй планшет в режиме **Это устройство — экран гостя** находит кассу-источник.',
            'Чтобы выйти из режима экрана гостя, сделайте тройной тап или долгое нажатие.',
          ),
        ],
      },
      {
        id: 'tv',
        title: 'Меню на телевизоре',
        blocks: [
          steps(
            'В админ-панели откройте **Настройки → Касса** и включите **Менюборд**. То же можно сделать на кассе: **Настройки → Телевизоры → Меню на ТВ**.',
            'Скопируйте ссылку для телевизора и откройте её в браузере телевизора.',
            'Выберите категории, включите или выключите фото и цены, задайте заголовок и интервал смены экрана — от 5 до 300 секунд.',
          ),
          p('Блюда, которые нельзя заказать, исчезают с экрана сами.'),
        ],
      },
    ],
  },

  'qr-menu': {
    lead: 'Электронное меню открывается в браузере телефона, без установки приложения. Гость выбирает блюда, оформляет заказ к столу или навынос и платит онлайн или на кассе, а заказ сразу появляется на кассе.',
    facts: [
      { label: 'Адрес', value: 'Домен вашего заведения' },
      { label: 'Оплата', value: 'Ваша ЮKassa или на кассе' },
      { label: 'Вход гостя', value: 'По телефону или email' },
    ],
    sections: [
      {
        id: 'domain',
        title: 'Адрес меню',
        blocks: [
          p('Меню открывается на домене, который вы привязали к заведению. HTTPS-сертификат выпускается автоматически.'),
          steps(
            'В админ-панели откройте **Настройки → Меню и бренд**, блок **Свой домен меню**, и впишите адрес, например `menu.mycafe.ru`.',
            'У регистратора домена добавьте DNS-запись CNAME на `menu.resstro.ru`. Значения для A-записи показаны в админ-панели под полем домена.',
            'Сохраните настройки. DNS обновляется за 5–15 минут, сертификат выпустится при первом заходе.',
          ),
          p('Сделайте QR-код с адресом меню и разместите его на столах.'),
          p('На вкладке **Меню и бренд** также настраиваются цвета, шрифт, иконка и значок сайта: меню выглядит как ваше, без упоминания Resstro.'),
        ],
      },
      {
        id: 'ordering',
        title: 'Как заказывает гость',
        blocks: [
          steps(
            'Гость открывает меню по QR-коду или ссылке, выбирает блюдо, добавки и количество и нажимает **В корзину**.',
            'В корзине нажимает **Оформить заказ** и входит по номеру телефона или email с кодом.',
            'Выбирает **В зале** и свободный столик или **Вынос** с телефоном и адресом, способ оплаты и нажимает **Подтвердить**.',
            'При онлайн-оплате переходит на страницу ЮKassa, а после оплаты возвращается в меню.',
          ),
          p('Статус заказа виден в разделе **Мои заказы**: принят, готовится, готов, выполнен.'),
          h3('Дозаказ'),
          p('Если у гостя уже есть заказ за столом, меню предложит **Добавить к моему заказу**. Дозаказ оплачивается на кассе.'),
        ],
      },
      {
        id: 'payments',
        title: 'Онлайн-оплата через ЮKassa',
        blocks: [
          p('Деньги поступают на ваш расчётный счёт через ваш магазин ЮKassa: Resstro в цепочке не участвует и комиссию не берёт.'),
          steps(
            'В личном кабинете ЮKassa откройте «Интеграция» → «Ключи API» и скопируйте shopId и секретный ключ.',
            'В админ-панели откройте **Настройки → Оплаты** и включите **Принимать оплату из меню**.',
            'Введите **shopId (номер магазина)** и **Секретный ключ** и нажмите **Сохранить**. Админ-панель проверит ключ в ЮKassa и покажет «Реквизиты подключены».',
            'В ЮKassa включите уведомления о событии `payment.succeeded` на адрес, который показан в админ-панели под формой.',
          ),
          note('Без реквизитов ЮKassa кнопка онлайн-оплаты гостю не показывается, оплатить можно только на кассе. Тестовый ключ помечается «Тестовый ключ»: с ним реальные деньги не списываются.'),
        ],
      },
      {
        id: 'visibility',
        title: 'Что видит гость',
        blocks: [
          p('Категории с выключенным **Активна в электронном меню** гостю не показываются. Блюдо с выключенным **Доступно для заказа** заказать нельзя.'),
        ],
      },
      {
        id: 'guest-profile',
        title: 'Профиль гостя',
        blocks: [
          p('В разделе **Профиль** гость видит свои заказы, адреса и сохранённые карты, может сменить имя, выйти или удалить аккаунт.'),
        ],
      },
    ],
  },

  people: {
    lead: 'Resstro People — приложение для гостей: найти заведение рядом, посмотреть меню, заказать к столу или с собой и забронировать стол. Приложение готовится к публикации в App Store.',
    facts: [
      { label: 'Платформа', value: 'iPhone' },
      { label: 'Статус', value: 'Скоро в App Store' },
      { label: 'Для заведения', value: 'Раздел **Приложение**' },
    ],
    sections: [
      {
        id: 'guest',
        title: 'Что может гость',
        blocks: [
          list(
            '**Главная**: заведения рядом, поиск, кухни и сортировка — ближе, по рейтингу, по алфавиту.',
            'Карточка заведения: меню по категориям и **Забронировать стол**.',
            'Заказ **С собой** или **За столом**, оплата **Онлайн** или **На месте**.',
            '**Заказы**: отменить заказ, пока его не взяли в работу, повторить или оценить.',
            '**Бронь**: дата, время и число гостей.',
            '**Профиль**: адреса, избранное, отзывы, уведомления.',
          ),
          p('Смотреть заведения и меню можно без входа. Для заказа, брони и отзывов гость входит по коду из письма. Оценить можно только завершённый заказ.'),
        ],
      },
      {
        id: 'venue',
        title: 'Для заведения',
        blocks: [
          list(
            'Карточка в приложении настраивается в админ-панели, в разделе **Приложение**.',
            'Заказы из Resstro People приходят на кассу так же, как из электронного меню, со звуковым сигналом.',
            'Ссылка на заведение вида `resstro.ru/v/…` открывает его в приложении, если оно установлено.',
          ),
        ],
      },
    ],
  },

  supplier: {
    lead: 'Кабинет поставщика нужен компаниям, которые продают продукты ресторанам и кафе. Ваш каталог появляется в магазине «Закуп» внутри касс заведений Resstro, а заявки приходят в кабинет.',
    facts: [
      { label: 'Адрес', value: '[supplier.resstro.ru](https://supplier.resstro.ru)' },
      { label: 'Стоимость', value: `${PARTNER_TRIAL} бесплатно, затем ${PARTNER_PRICE}/мес` },
      { label: 'О продукте', value: '[Поставщикам](/suppliers)' },
    ],
    sections: [
      {
        id: 'signup',
        title: 'Регистрация',
        blocks: [
          steps(
            'На [supplier.resstro.ru](https://supplier.resstro.ru) откройте регистрацию поставщика: название компании, email, пароль, город и телефон.',
            'Нажмите **Зарегистрироваться**. Аккаунт пройдёт модерацию: статус «На модерации» сменится на «Активен».',
          ),
        ],
      },
      {
        id: 'launch',
        title: 'Запуск магазина',
        blocks: [
          p('На странице **Главная** кабинета есть чек-лист запуска.'),
          steps(
            'Заполните реквизиты компании в разделе **Профиль**: основные данные, юридические и банковские реквизиты.',
            'Добавьте категории товаров в разделе **Категории**.',
            'Добавьте товары: **Товары → Новый товар** — название, цена, единицы и упаковка.',
            'В **Профиль → Доставка и условия** укажите регионы доставки, минимальный заказ и порог бесплатной доставки.',
          ),
          p('Когда всё заполнено, на главной появится «Магазин готов».'),
        ],
      },
      {
        id: 'orders',
        title: 'Заявки',
        blocks: [
          p('Новые заявки от заведений отмечены счётчиком у раздела **Заявки**. Фильтры: **Все**, **Новые**, **Подтверждённые**, **Отгруженные**, **Доставленные**.'),
          steps(
            'Откройте заявку и нажмите **Подтвердить** или отклоните её.',
            'Когда товар отправлен, нажмите **Отгрузить**.',
            'Получили деньги — отметьте оплату в карточке заявки.',
          ),
        ],
      },
      {
        id: 'payment-terms',
        title: 'Условия оплаты и долги',
        blocks: [
          p('Условия оплаты для заведений: **Предоплата**, **Оплата при доставке** или **Отсрочка платежа** с числом дней.'),
          p('Раздел **Долги** показывает текущий долг заведений, просрочку, общий оборот и неоплаченные заказы со статусами «Не оплачен», «Частично», «Просрочен» и «Оплачен».'),
          note('Расчёты между заведением и поставщиком идут напрямую, Resstro деньги не проводит.'),
        ],
      },
      {
        id: 'integrations',
        title: 'Интеграции',
        blocks: [
          p('Раздел **Интеграции** подключает 1С и другие учётные системы: заявки можно вести прямо в них.'),
        ],
      },
      {
        id: 'subscription',
        title: 'Подписка',
        blocks: [
          p('В разделе **Подписка** видны статус и дата окончания, там же кнопки **Оплатить подписку** или **Продлить на месяц**, **Отключить авто-продление** и история платежей. Оплата проходит через ЮKassa.'),
          warn('Без активной подписки каталог скрыт из магазина «Закуп».'),
        ],
      },
      {
        id: 'restaurant',
        title: 'Как заказывает заведение',
        blocks: [
          steps(
            'Магазин «Закуп» включён в кассах заведений по умолчанию. Владелец заведения может отключить его в админ-панели: **Настройки → Касса**.',
            'На кассе кассир открывает **Закуп → Магазин**, выбирает поставщика и собирает корзину.',
            'Кассир оформляет заказ, а при получении принимает товар. Поставка попадает на склад заведения.',
          ),
        ],
      },
    ],
  },

  wholesaler: {
    lead: 'Resstro Оптовик — отдельный кабинет для фабрик-кухонь, пекарен, производителей полуфабрикатов и дистрибьюторов. Им удобно пользоваться с телефона: продажи, склад, приходы, расходы и прибыль в одном месте. Касса для работы не нужна.',
    facts: [
      { label: 'Адрес', value: '[wholesaler.resstro.ru](https://wholesaler.resstro.ru)' },
      { label: 'Стоимость', value: `${PARTNER_TRIAL} бесплатно, затем ${PARTNER_PRICE}/мес` },
      { label: 'О продукте', value: '[Оптовикам](/wholesalers)' },
    ],
    sections: [
      {
        id: 'signup',
        title: 'Регистрация и вход',
        blocks: [
          p('Зарегистрируйтесь на [wholesaler.resstro.ru](https://wholesaler.resstro.ru) по email и паролю. Дальше вход — кнопкой **Войти**.'),
        ],
      },
      {
        id: 'navigation',
        title: 'Разделы',
        blocks: [
          list(
            '**Главная**, **Аналитика**, **Финансы** и **Расходы** — деньги и показатели.',
            'Торговля: **Продажи**, **Возвраты**, **Курьеры**.',
            'Склад и закупки: **Товары**, **Склад**, **Документы**, **Поставщики**.',
            'Команда: **Чат**. Аккаунт: **Подписка**, **Профиль**.',
          ),
        ],
      },
      {
        id: 'catalog',
        title: 'Товары и тех-карты',
        blocks: [
          steps(
            'Откройте **Товары → Новый товар** и выберите тип: товар, заготовка или ингредиент. Разложите товары по категориям.',
            'Для своей продукции соберите тех-карту: выход партии и ингредиенты. Себестоимость посчитается сама.',
            'Когда партия произведена, нажмите **Выпустить партию**.',
          ),
        ],
      },
      {
        id: 'incoming',
        title: 'Приходы и поставщики',
        blocks: [
          steps(
            'Откройте **Документы → Новый приход**, выберите поставщика и позиции. Приход сохранится черновиком.',
            'Проведите приход — склад пополнится.',
            'Отметьте оплату прихода. Долг перед поставщиком виден в разделе **Поставщики**.',
          ),
          p('Новый поставщик добавляется в **Поставщики → Новый поставщик**.'),
        ],
      },
      {
        id: 'sales',
        title: 'Продажи и накладные',
        blocks: [
          steps(
            'Откройте **Продажи → Новая продажа**: покупатель, позиции и оплата.',
            'Откройте накладную или чек и нажмите **Печать**: печать идёт через обычный диалог печати браузера.',
          ),
          p('У накладной есть ссылка, которую можно отправить покупателю.'),
          h3('Возвраты и обмены'),
          p('**Возвраты → Новый возврат** или **Новый обмен**. Возврат денег уменьшает выручку, обмен — нет.'),
        ],
      },
      {
        id: 'stock',
        title: 'Склад',
        blocks: [
          p('В разделе **Склад** оформляются **Новое списание** и **Новая инвентаризация**. На главной есть блок «Заканчивается на складе» с кнопкой **Оформить приход**.'),
        ],
      },
      {
        id: 'money',
        title: 'Финансы и аналитика',
        blocks: [
          p('На странице **Главная** — выручка, продажи и валовая прибыль за сегодня, а за 30 дней ещё себестоимость, расходы, чистая прибыль, средний чек и топ товаров. Раздел **Финансы** показывает движение денег, **Расходы** — затраты бизнеса.'),
        ],
      },
      {
        id: 'couriers',
        title: 'Курьеры',
        blocks: [
          p('Раздел **Курьеры**: кто сейчас на линии, накладные, курьеры, маршруты, аналитика и долги точек. Маршруты задаются заранее: город, маршрут, точки.'),
          p('Курьера добавляет владелец — почта и имя. Дальше курьер работает в своём приложении: [Приложение курьера](/docs/courier).'),
        ],
      },
    ],
  },

  courier: {
    lead: 'Приложение курьера открывается в браузере телефона и добавляется на главный экран. Курьер берёт маршрут, отмечает точки, принимает деньги и записывает долги, а всё сразу попадает в учёт оптовика.',
    facts: [
      { label: 'Адрес', value: '[courier.resstro.ru](https://courier.resstro.ru)' },
      { label: 'Вход', value: 'Код из письма' },
      { label: 'Кто добавляет', value: 'Владелец в Оптовике' },
    ],
    sections: [
      {
        id: 'login',
        title: 'Вход',
        blocks: [
          steps(
            'Владелец добавляет курьера в разделе **Курьеры** кабинета Оптовика: почта и имя.',
            'Курьер открывает [courier.resstro.ru](https://courier.resstro.ru), нажимает **Получить код** и вводит код из письма.',
          ),
        ],
      },
      {
        id: 'start',
        title: 'Начало смены',
        blocks: [
          steps(
            'Нажмите **Начать работу**.',
            'Выберите город и маршрут. Занятый маршрут помечен «Едет» и именем курьера. Если для вас есть накладная, откройте её и нажмите **Начать маршрут**.',
            'Подтвердите **Начать смену**. Приложение проверит геолокацию.',
          ),
          p('Можно поехать и без накладной: **Маршрут без накладной**.'),
        ],
      },
      {
        id: 'route',
        title: 'Маршрут',
        blocks: [
          p('Вверху итоги смены: **Отгружено**, **Возвраты**, **Получено**, **Долг** и сколько точек пройдено. У точки с долгом стоит метка с его суммой. Если вы едете с накладной, видно, сколько позиций ещё в машине.'),
        ],
      },
      {
        id: 'point',
        title: 'На точке',
        blocks: [
          steps(
            'Откройте точку, когда вы рядом с ней: отметить визит можно только в радиусе точки.',
            'Внесите отгрузку и возвраты, а в блоке **Оплата** — полученную сумму. Если денег меньше, разница станет долгом точки.',
            'При необходимости сфотографируйте витрину.',
          ),
          p('**Обмен** — возврат товара на замену, деньги при этом не двигаются.'),
          note('Возвращённый товар списывается как негодный и на склад не возвращается.'),
        ],
      },
      {
        id: 'finish',
        title: 'Завершение маршрута',
        blocks: [
          steps(
            'Нажмите **Завершить маршрут**, проверьте итоги и при необходимости оставьте комментарий руководителю.',
            'Если маршрут был с накладной, пересчитайте остаток в машине и нажмите **Сдать остаток и завершить**.',
          ),
        ],
      },
      {
        id: 'offline',
        title: 'Без связи',
        blocks: [
          p('Если пропала сеть, приложение пишет «Нет сети — всё сохраним и отправим, как появится связь». Неотправленные визиты помечены «Ждёт отправки», их можно отправить кнопкой **Отправить**.'),
        ],
      },
      {
        id: 'stats',
        title: 'Аналитика',
        blocks: [
          p('Вкладка **Аналитика** показывает, сколько продано и получено и какой долг накопился за период.'),
        ],
      },
    ],
  },
};
